import { LinkOutlined } from '@ant-design/icons'
import { Modal, Space } from 'antd';
import { ListResponseTrackerBean, TrackerBean } from 'common/api/api.ts';
import { trackerApi } from 'common/api/entitiesApi.tsx';
import { ListActionButton } from 'common/components/ListActionButton.tsx';
import { ListCard } from 'common/components/ListCard.tsx';
import { AdvertiserSelector } from 'common/components/reloadable/AdvertiserSelector.tsx';
import { SearchInput } from 'common/components/SearchInput.tsx';
import { ListFilters, useAppContext } from 'common/context/AppContext.tsx';
import { CommonRoutes } from 'common/context/commonRoutes.ts';
import { COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH } from 'common/util/constants';
import { initListFilters, useListFilters } from 'common/util/useListFilters.ts';
import { apiCall, formatMoney } from 'common/util/Util.tsx';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import TrackerCode from './TrackerCode.tsx';

const TrackerList = ({agency}: { agency: boolean }) => {
    const appContext = useAppContext()

    const [filters, setFilters] = useState<ListFilters>(initListFilters())
    const {setFilter} = useListFilters(setFilters)

    const [loading, setLoading] = useState<boolean>(false)
    const [listData, setListData] = useState<ListResponseTrackerBean>()
    const [showUrlModal, setShowUrlModal] = useState(false)
    const [selectedTracker, setSelectedTracker] = useState<TrackerBean | null>(null)

    const showTrackerUrl = (record: TrackerBean) => {
        setSelectedTracker(record)
        setShowUrlModal(true)
    }

    const updateTrackerInList = (tracker: TrackerBean) => {
        setSelectedTracker(tracker)
        setListData(current => current
            ? {
                ...current,
                data: current.data?.map(item => item.data?.id === tracker.id ? {...item, data: tracker} : item)
            }
            : current
        )
    }

    const loadData = () => {
        apiCall(
            setLoading,
            appContext,
            () =>
                trackerApi.trackerList(
                    filters.sort?.name,
                    filters.sort?.direction,
                    filters.name,
                    filters.advertiserId,
                    {query: {page: filters.page, size: filters.pageSize}}
                ),
            (data) => setListData(data)
        )
    }

    return (
        <>
            <ListCard
                loading={loading}
                title="Trackers"
                createName="Tracker"
                createRoute={CommonRoutes.TRACKER_CREATE}
                listRouteKey={CommonRoutes.TRACKER}
                filters={filters}
                setFilters={setFilters}
                listData={listData}
                loadData={loadData}
                columns={[
                    {
                        title: 'ID',
                        dataIndex: 'id',
                        width: COLUMN_ID_WIDTH
                    },
                    {
                        title: 'Name',
                        dataIndex: 'name',
                        sorter: true,
                        className: 'list-table-name',
                        ellipsis: true,
                        render: (text: string, record: TrackerBean) => (
                            <Link style={{color: '#5888CF', minWidth: 100}} to={`/tracker/${record.id}/edit`}>
                                {text}
                            </Link>
                        ),
                        width: COLUMN_NAME_WIDTH
                    },
                    {
                        title: 'Token',
                        dataIndex: 'token',
                        width: COLUMN_ID_WIDTH,
                    },
                    ...(agency
                        ? [
                            {
                                title: 'Advertiser',
                                dataIndex: 'advertiserName',
                                sorter: true,
                                ellipsis: true,
                                width: 120
                            }
                        ]
                        : []),
                    {
                        title: 'Type',
                        dataIndex: 'trackerType',
                        sorter: true,
                        width: 90
                    },
                    {
                        title: 'Event',
                        dataIndex: 'eventName',
                        sorter: true,
                        width: 90
                    },
                    {
                        title: 'Price',
                        dataIndex: 'cost',
                        width: 90,
                        render: (value: number) => '$' + formatMoney(value || 0)
                    },
                    {
                        title: 'Last Modified',
                        dataIndex: 'modified',
                        sorter: true,
                        width: 100,
                        render: (date) => date && dayjs(date).format('DD MMM YY')
                    },
                    {
                        title: 'Actions',
                        key: 'actions',
                        width: 130,
                        render: (_: any, record: TrackerBean) => (
                            <Space>
                                <ListActionButton tooltip="Show Code" onClick={() => showTrackerUrl(record)}>
                                    <LinkOutlined/>
                                </ListActionButton>
                            </Space>
                        )
                    }
                ]}
            >
                <SearchInput placeholder="Search by ID, Name, Token" width="250px" value={filters.name}
                             onChange={(value) => setFilter('name', value)}/>
                {agency && (
                    <AdvertiserSelector
                        placeholder="Filter by Advertiser"
                        style={{minWidth: '200px'}}
                        value={filters.advertiserId}
                        onChange={(value: number) => setFilter('advertiserId', value)}
                    />
                )}
            </ListCard>

            <Modal
                open={showUrlModal}
                title={`${selectedTracker?.name || ''}`}
                cancelText="Close"
                onCancel={() => {
                    setShowUrlModal(false)
                    setSelectedTracker(null)
                }}
                okButtonProps={{style: {display: 'none'}}}
                width={'50%'}
            >
                {selectedTracker && <TrackerCode tracker={selectedTracker} onTrackerChange={updateTrackerInList}/>}
            </Modal>
        </>
    )
}

export default TrackerList
